/**
 * Microphone capture via getUserMedia + AnalyserNode.
 *
 * Requests the microphone with all processing disabled
 * (echo cancellation, noise suppression, auto gain control).
 */

export interface CaptureNode {
  context: AudioContext;
  analyser: AnalyserNode;
  source: MediaStreamAudioSourceNode;
  stream: MediaStream;
  fftData: Float32Array<ArrayBuffer>;
}

const FFT_SIZE = 16384;

/**
 * Request microphone access and set up the audio capture pipeline.
 * Returns the AudioContext, AnalyserNode, and a pre-allocated buffer.
 */
export async function startCapture(): Promise<CaptureNode> {
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: {
      echoCancellation: false,
      noiseSuppression: false,
      autoGainControl: false,
      channelCount: 1,
    },
  });

  const context = new AudioContext();
  const source = context.createMediaStreamSource(stream);

  const analyser = context.createAnalyser();
  analyser.fftSize = FFT_SIZE;
  analyser.smoothingTimeConstant = 0; // We do our own averaging
  analyser.minDecibels = -120;
  analyser.maxDecibels = 0;

  source.connect(analyser);
  // Do NOT connect analyser to destination — we don't want mic playback

  const fftData = new Float32Array(analyser.frequencyBinCount) as Float32Array<ArrayBuffer>;

  return { context, analyser, source, stream, fftData };
}

/**
 * Read the current FFT data from the analyser into the pre-allocated buffer.
 */
export function readFftData(capture: CaptureNode): Float32Array {
  capture.analyser.getFloatFrequencyData(capture.fftData);
  return capture.fftData;
}

/**
 * Stop capture and release resources.
 */
export function stopCapture(capture: CaptureNode): void {
  capture.stream.getTracks().forEach((t) => t.stop());
  capture.source.disconnect();
  capture.analyser.disconnect();
  capture.context.close();
}

/**
 * Get actual applied audio constraints for transparency.
 */
export function getAppliedSettings(
  capture: CaptureNode
): MediaTrackSettings | null {
  const tracks = capture.stream.getAudioTracks();
  if (tracks.length === 0) return null;
  return tracks[0].getSettings();
}
